import { useState, useRef, useEffect } from "react";

interface Message {
  id: string;
  sender: "boss" | "employee";
  senderName: string;
  text: string;
  time: string;
  read: boolean;
}

interface Conversation {
  id: string;
  applicantName: string;
  applicantRole: string;
  applicantAvatar: string;
  jobTitle: string;
  status: "new" | "reviewing" | "interview" | "hired" | "rejected";
  lastMessage: string;
  lastTime: string;
  unread: number;
  messages: Message[];
}

const mockConversations: Conversation[] = [
  {
    id: "c1",
    applicantName: "Sarah Johnson",
    applicantRole: "Applied for Warehouse Operations Manager",
    applicantAvatar: "https://public.readdy.ai/ai/img_res/e36ad7bd9538d60d0a48b6800b244c5d.jpg",
    jobTitle: "Warehouse Operations Manager",
    status: "interview",
    lastMessage: "Thank you! I am available Tuesday at 2pm.",
    lastTime: "10 min ago",
    unread: 2,
    messages: [
      { id: "m1", sender: "employee", senderName: "Sarah Johnson", text: "Hello! I just submitted my application for the Warehouse Operations Manager role. I have 5 years of experience in logistics and warehouse management. Looking forward to hearing from you!", time: "Yesterday 9:00 AM", read: true },
      { id: "m2", sender: "boss", senderName: "TechVision HR", text: "Hi Sarah! Thank you for applying. We reviewed your resume and we are very impressed with your background. We would love to schedule an interview. Are you available this week?", time: "Yesterday 2:30 PM", read: true },
      { id: "m3", sender: "employee", senderName: "Sarah Johnson", text: "Absolutely! I am flexible most days. Would Tuesday or Wednesday work for you?", time: "Yesterday 3:15 PM", read: true },
      { id: "m4", sender: "boss", senderName: "TechVision HR", text: "Tuesday works great! How about 2:00 PM PST? We will do a 30-minute video call via Zoom.", time: "Today 9:00 AM", read: true },
      { id: "m5", sender: "employee", senderName: "Sarah Johnson", text: "Thank you! I am available Tuesday at 2pm.", time: "10 min ago", read: false },
    ],
  },
  {
    id: "c2",
    applicantName: "Marcus Williams",
    applicantRole: "Applied for E-Commerce Sales Rep",
    applicantAvatar: "https://readdy.ai/api/search-image?query=young%20professional%20man%20smiling%20headshot%20portrait%20clean%20white%20background&width=60&height=60&seq=applicant2&orientation=squarish",
    jobTitle: "E-Commerce Sales Representative",
    status: "reviewing",
    lastMessage: "I have 3 years of B2B sales experience and...",
    lastTime: "2 hours ago",
    unread: 1,
    messages: [
      { id: "m1", sender: "employee", senderName: "Marcus Williams", text: "Hi there! I am very excited about the E-Commerce Sales Representative position. I have 3 years of B2B sales experience and have consistently exceeded my targets by 120%.", time: "2 hours ago", read: false },
    ],
  },
  {
    id: "c3",
    applicantName: "Lily Chen",
    applicantRole: "Applied for Social Media Content Creator",
    applicantAvatar: "https://readdy.ai/api/search-image?query=young%20asian%20woman%20smiling%20headshot%20portrait%20clean%20white%20background%20creative&width=60&height=60&seq=applicant3&orientation=squarish",
    jobTitle: "Social Media Content Creator",
    status: "hired",
    lastMessage: "I am so excited to join the team! See you Monday!",
    lastTime: "1 day ago",
    unread: 0,
    messages: [
      { id: "m1", sender: "employee", senderName: "Lily Chen", text: "Hi! I am a content creator with 200K followers on TikTok. I would love to bring my audience and creativity to PureGlow Labs!", time: "3 days ago", read: true },
      { id: "m2", sender: "boss", senderName: "PureGlow HR", text: "Lily, your portfolio is amazing! We would love to offer you the position. Can you start next Monday?", time: "2 days ago", read: true },
      { id: "m3", sender: "employee", senderName: "Lily Chen", text: "I am so excited to join the team! See you Monday!", time: "1 day ago", read: true },
    ],
  },
  {
    id: "c4",
    applicantName: "James Rodriguez",
    applicantRole: "Applied for Logistics Coordinator",
    applicantAvatar: "https://readdy.ai/api/search-image?query=young%20hispanic%20man%20smiling%20headshot%20portrait%20clean%20white%20background%20professional&width=60&height=60&seq=applicant4&orientation=squarish",
    jobTitle: "Logistics Coordinator",
    status: "new",
    lastMessage: "I attached my resume and cover letter. Please let me know...",
    lastTime: "5 hours ago",
    unread: 1,
    messages: [
      { id: "m1", sender: "employee", senderName: "James Rodriguez", text: "Good afternoon! I am applying for the Logistics Coordinator position. I attached my resume and cover letter. Please let me know if you need any additional information.", time: "5 hours ago", read: false },
    ],
  },
];

const statusColors: Record<string, string> = {
  new: "bg-sky-100 text-sky-700",
  reviewing: "bg-yellow-100 text-yellow-700",
  interview: "bg-orange-100 text-orange-700",
  hired: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

interface JobMessagingPanelProps {
  onClose: () => void;
  viewAs: "boss" | "employee";
}

export default function JobMessagingPanel({ onClose, viewAs }: JobMessagingPanelProps) {
  const [conversations, setConversations] = useState(mockConversations);
  const [activeId, setActiveId] = useState("c1");
  const [newMsg, setNewMsg] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const active = conversations.find((c) => c.id === activeId)!;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeId, conversations]);

  const sendMessage = () => {
    if (!newMsg.trim()) return;
    const msg: Message = {
      id: `m${Date.now()}`,
      sender: viewAs,
      senderName: viewAs === "boss" ? "You (HR Manager)" : "You",
      text: newMsg.trim(),
      time: "Just now",
      read: true,
    };
    setConversations((prev) =>
      prev.map((c) =>
        c.id === activeId
          ? { ...c, messages: [...c.messages, msg], lastMessage: newMsg.trim(), lastTime: "Just now", unread: 0 }
          : c
      )
    );
    setNewMsg("");
  };

  const updateStatus = (status: Conversation["status"]) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === activeId ? { ...c, status } : c))
    );
  };

  const markRead = (id: string) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, unread: 0, messages: c.messages.map((m) => ({ ...m, read: true })) } : c
      )
    );
    setActiveId(id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }}>
      <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-orange-100 rounded-xl">
              <i className="ri-message-3-line text-orange-500" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-gray-900">Job Messaging</h2>
              <p className="text-xs text-gray-400">
                {viewAs === "boss" ? "Viewing as: Employer / HR Manager" : "Viewing as: Job Applicant"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-gray-600" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar — Conversations */}
          <div className="w-72 border-r border-gray-100 flex flex-col flex-shrink-0 overflow-y-auto">
            <div className="p-3 border-b border-gray-50">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input className="w-full pl-8 pr-3 py-2 bg-gray-50 rounded-xl text-xs focus:outline-none" placeholder="Search applicants..." />
              </div>
            </div>
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => markRead(conv.id)}
                className={`w-full text-left p-4 border-b border-gray-50 hover:bg-orange-50 transition-colors cursor-pointer ${activeId === conv.id ? "bg-orange-50 border-l-2 border-l-orange-500" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    <img src={conv.applicantAvatar} alt={conv.applicantName} className="w-10 h-10 rounded-full object-cover object-top" />
                    {conv.unread > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full">{conv.unread}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-bold text-gray-900 truncate">{conv.applicantName}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0 ml-1">{conv.lastTime}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mb-1">{conv.lastMessage}</p>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[conv.status]}`}>
                      {conv.status.charAt(0).toUpperCase() + conv.status.slice(1)}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Chat Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <img src={active.applicantAvatar} alt={active.applicantName} className="w-10 h-10 rounded-full object-cover object-top" />
                <div>
                  <p className="text-sm font-bold text-gray-900">{active.applicantName}</p>
                  <p className="text-xs text-gray-400">{active.applicantRole}</p>
                </div>
              </div>
              {viewAs === "boss" && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-medium">Status:</span>
                  <select
                    value={active.status}
                    onChange={(e) => updateStatus(e.target.value as Conversation["status"])}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none cursor-pointer bg-white"
                  >
                    <option value="new">New</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="interview">Interview</option>
                    <option value="hired">Hired</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {active.messages.map((msg) => {
                const isMe = msg.sender === viewAs;
                return (
                  <div key={msg.id} className={`flex gap-3 ${isMe ? "flex-row-reverse" : ""}`}>
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-bold ${isMe ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-600"}`}>
                      {msg.senderName.charAt(0)}
                    </div>
                    <div className={`max-w-xs lg:max-w-md ${isMe ? "items-end" : "items-start"} flex flex-col`}>
                      <span className={`text-xs text-gray-400 mb-1 ${isMe ? "text-right" : ""}`}>{msg.senderName}</span>
                      <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${isMe ? "bg-orange-500 text-white rounded-tr-sm" : "bg-gray-100 text-gray-800 rounded-tl-sm"}`}>
                        {msg.text}
                      </div>
                      <span className="text-xs text-gray-400 mt-1">{msg.time}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-end gap-3">
                <textarea
                  value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  rows={2}
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 resize-none"
                  placeholder={viewAs === "boss" ? "Reply to applicant..." : "Send a message to the employer..."}
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMsg.trim()}
                  className="w-11 h-11 flex items-center justify-center bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <i className="ri-send-plane-fill text-base" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">Press Enter to send · Shift+Enter for new line</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
